import React from 'react'
import ClassNames from 'classnames'
import styled from 'styled-components'
import * as moment from 'moment'
import * as interfaces from '../../../../../static/interfaces'
import * as constants from '../../../../../static/constants'
import Typography from 'components/atoms/_layouts/Typography'
import SvgIcon from 'components/atoms/SvgIcon'
import {
  getAges,
  getLimitParticipantNote,
  getPriceBreakdown,
  ISelectedGuests,
  isStartTimeAvailable,
} from 'utils/reservation'
import { utilService } from 'core/services'
import { StartTimeSelectButton } from './ReservationStartTime/StartTimeSelectButton'
import StartTimeDetail from './StartTimeDetail'

interface IStartTimesProps {
  date: { date: moment.Moment }
  post: interfaces.IPost
  memberPost: any
  startTimes: interfaces.IStartTime[]
  guests: ISelectedGuests
  nonMemberCanBuy: boolean
  isPostInFamily?: boolean
  initialOpen?: boolean
  showPaymentForm: (post, reservation) => void
}

export const ReservationStartTimes = React.memo(
  ({
    date,
    post,
    memberPost,
    startTimes,
    guests,
    nonMemberCanBuy,
    isPostInFamily,
    initialOpen = false,
    showPaymentForm,
  }: IStartTimesProps) => {
    const isPrivate = memberPost.post_type === 'private'
    const ages = getAges(memberPost?.post_prices)
    const guestCount = guests.adults + guests.children
    const prices = getPriceBreakdown(memberPost.post_prices, guests, isPrivate)

    // 予約可能なものを先頭に並べ、それぞれ時間順
    const sortedStartTimes = React.useMemo(
      () =>
        [...startTimes].sort((a, b) => {
          const aAvailable = isStartTimeAvailable(a, guestCount)
          const bAvailable = isStartTimeAvailable(b, guestCount)
          if (aAvailable && !bAvailable) {
            return -1
          }
          if (!aAvailable && bAvailable) {
            return 1
          }
          return a.start_time.localeCompare(b.start_time)
        }),
      [startTimes, guestCount]
    )
    const firstAvailableStartTime = sortedStartTimes.find(startTime =>
      isStartTimeAvailable(startTime, guestCount)
    )
    const [selectedStartTime, setSelectedStartTime] = React.useState<interfaces.IStartTime | null>(
      firstAvailableStartTime || null
    )
    const [open, setOpen] = React.useState(initialOpen)

    React.useEffect(() => {
      setSelectedStartTime(current => {
        if (current && isStartTimeAvailable(current, guestCount)) {
          return current
        }
        return firstAvailableStartTime || null
      })
    }, [firstAvailableStartTime, guestCount])

    const hasLimitParticipantTypeGuest = React.useMemo(() => {
      if (isPrivate) {
        return false
      }
      if (memberPost.limit_participant_type === 'child') {
        return guests.children > 0 || guests.infants > 0
      }
      if (memberPost.limit_participant_type === 'infant') {
        return guests.infants > 0
      }
      return false
    }, [isPrivate, memberPost.limit_participant_type, guests.children, guests.infants])

    return (
      <StartTimeGroup className={ClassNames({ open })}>
        <SvgIcon
          className="StartTime_Toggle_Button"
          name={open ? 'expand_less' : 'expand_more'}
          role="button"
          onClick={() => setOpen(old => !old)}
        />
        <div className="StartTime_Info">
          {isPostInFamily && <div className="TourName bold">{memberPost.name}</div>}
          <div className={isPostInFamily ? 'TourName' : 'TourName bold'}>
            {isPostInFamily ? post.name : memberPost.name}
          </div>
        </div>
        {isPostInFamily && !isPrivate && (
          <div className="PerGroup_Number">
            <SvgIcon name="people" className="icon" ariaHidden={true} role="presentation" />
            <Typography type="body" size="md">
              Maximum Travelers per Group : <span>{memberPost.max_people}</span>
            </Typography>
          </div>
        )}
        {memberPost.limit_participant_type && (
          <div className="LimitAge">
            <Typography type="subtitle" size="lg" color="caution">
              {getLimitParticipantNote(memberPost, ages)}
            </Typography>
          </div>
        )}
        <div className={ClassNames('StartTime_Detail_Wrapper', { open })}>
          <StartTimeDetail
            memberPost={memberPost}
            isPrivate={isPrivate}
            ages={ages}
            guests={guests}
            prices={prices}
          />
        </div>
        <button className="StartTime_Toggle" type="button" onClick={() => setOpen(old => !old)}>
          {open ? 'show less ▲' : 'show more ▼'}
        </button>
        <div className="Time_List">
          {sortedStartTimes.map(startTime => {
            const available = isStartTimeAvailable(startTime, guestCount)
            return (
              <StartTimeSelectButton
                key={`${startTime.post_id}-${startTime.start_time}`}
                available={available}
                startTime={startTime}
                guests={guests}
                selectedStartTime={selectedStartTime}
                setSelectedStartTime={setSelectedStartTime}
              />
            )
          })}
        </div>
        <div className="Price">
          <div className="Price_Left">
            <p className="StartTime_SubLabel">Total Price</p>
            <div className="PriceLabel">
              <span>¥</span>
              <span className="PriceLabel_Price">
                {utilService.separate(
                  String(
                    isPrivate
                      ? prices.private.price
                      : prices.adult.calcPrice + prices.child.calcPrice + prices.infant.calcPrice
                  )
                )}
              </span>
            </div>
            <span className="text">All taxes and fees included</span>
          </div>
          <button
            className="Choose_Button"
            disabled={!selectedStartTime || !nonMemberCanBuy || hasLimitParticipantTypeGuest}
            onClick={() =>
              showPaymentForm(memberPost, {
                selectedDate: date.date,
                totalPrice: isPrivate
                  ? prices.private.price
                  : prices.adult.calcPrice + prices.child.calcPrice + prices.infant.calcPrice,
                departure: selectedStartTime,
              })
            }
          >
            Choose
          </button>
        </div>
      </StartTimeGroup>
    )
  }
)

const StartTimeGroup = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 0.8rem;
  padding: 1.6rem;
  &.open {
    border-color: ${constants.COLORS.primary};
  }

  .StartTime_Info {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding-right: 4rem;
  }
  .TourName {
    font-size: 1.4rem;
    line-height: 2rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 1.3rem;
      line-height: 1.8rem;
    }
    &.bold {
      line-height: 2.4rem;
      font-size: 1.8rem;
      font-weight: bold;
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        font-size: 1.6rem;
      }
    }
  }
  .StartTime {
    color: ${constants.COLORS.primary};
    font-weight: bold;
  }
  .StartTime_Toggle_Button {
    position: absolute;
    top: 1.6rem;
    right: 1.6rem;
    width: 3.2rem;
    height: 3.2rem;
    padding: 0.4rem;
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 50%;
    cursor: pointer;
    transition: 0.25s border-color ease;
    &:hover {
      border-color: ${constants.COLORS.primary};
    }
  }
  .StartTime_Detail_Wrapper {
    display: grid;
    grid-template-rows: 0fr;
    transition: 0.25s grid-template-rows ease;
    &.open {
      grid-template-rows: 1fr;
    }
    > div {
      overflow: hidden;
      > div:last-child {
        margin-bottom: 1.6rem;
        @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
          margin-bottom: 1.2rem;
        }
      }
    }
  }
  .StartTime_Toggle {
    display: inline-block;
    margin-top: 0.4rem;
    padding: 0;
    border: none;
    background: transparent;
    color: ${constants.COLORS.primary};
    font: inherit;
    font-size: 1.4rem;
    cursor: pointer;
    text-decoration: underline;
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        text-decoration: none;
      }
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      font-size: 1.3rem;
    }
  }
  .Price .StartTime_SubLabel {
    line-height: 2rem;
    font-size: 1.4rem;
    font-weight: bold;
    color: ${constants.COLORS.subText};
  }
  .PerGroup_Number {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.4rem;
    line-height: 1.6rem;
    span {
      font-weight: bold;
    }
    .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      color: ${constants.COLORS.subText};
    }
  }
  .LimitAge {
    padding-top: 0.4rem;
    line-height: 2rem;
  }

  .Time_List {
    margin-top: 2rem;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.6rem;
    /* PC4列, スマホ3列 */
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      grid-template-columns: repeat(3, 1fr);
    }
    button {
      white-space: nowrap;
      transition: 0.25s background ease, 0.25s color ease;
      &:not(.closed, .selected):hover {
        background: ${constants.COLORS.primary};
        color: ${constants.COLORS.white};
        cursor: pointer;
      }
    }
  }

  .Price {
    display: flex;
    align-items: end;
    justify-content: space-between;
    margin-top: 1.6rem;
  }
  .Price_Left {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .PriceLabel_Price {
    font-size: 2rem;
    font-weight: bold;
  }
  .text {
    font-size: 1rem;
    font-weight: normal;
  }
  .Choose_Button {
    width: 16rem;
    height: 4.8rem;
    border: none;
    border-radius: 1.2rem;
    background: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
    font-size: 1.6rem;
    font-weight: bold;
    cursor: pointer;
    transition: 0.25s background ease;
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${constants.COLORS.secondary};
      }
    }
    &:disabled {
      background: ${constants.COLORS.disabled};
      cursor: not-allowed;
    }
  }
`
